import { useSyncExternalStore } from 'react';

import type { DroppedElement } from '@/api/tolerant';

/**
 * Rows the dashboard could not read, kept where a person can see them.
 *
 * The parse layer drops a malformed row rather than failing the screen, and
 * that trade is only defensible if the drop is visible. A list quietly one row
 * short is a worse bug than a loud one: it looks like data, it gets copied into
 * a report, and nobody finds out until a number is wrong somewhere expensive.
 *
 * So every drop lands here, the shell shows that it happened, and the detail
 * says exactly which field of which row the servers and this build disagree
 * about. That detail is the same thing the old fatal error carried; what has
 * changed is that it no longer takes the screen with it.
 *
 * Deliberately a module store rather than context. It is written from the fetch
 * layer, which has no component to hang state off, and read by one badge.
 */

export interface DriftRecord extends DroppedElement {
  /** The request whose response the row came from. */
  readonly path: string;
  readonly at: number;
}

/** Bounded, because a persistently broken field would otherwise grow forever. */
const MAX_RECORDS = 50;

let records: readonly DriftRecord[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function recordDrift(path: string, dropped: readonly DroppedElement[]): void {
  if (dropped.length === 0) return;
  const now = Date.now();
  records = [...records, ...dropped.map((drop) => ({ ...drop, path, at: now }))].slice(
    -MAX_RECORDS,
  );
  // Always, not only in development. A row the product could not read is a
  // defect report, and the console is where the person debugging it looks.
  // eslint-disable-next-line no-console
  console.warn(
    `[zelisight] dropped ${String(dropped.length)} unreadable row(s) from ${path}`,
    dropped,
  );
  emit();
}

export function clearDrift(): void {
  if (records.length === 0) return;
  records = [];
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): readonly DriftRecord[] {
  return records;
}

export function useDrift(): readonly DriftRecord[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
